import React, {
  useCallback,
  useState,
} from 'react';
import EmojiPicker from 'emoji-picker-react';
import PropTypes from 'prop-types';
import { observer } from 'mobx-react';

import {
  ClickAwayListener,
} from '@mui/material';

import {
  StyledEmojiIcon,
  StyledPopper,
} from './styles';

const SkinToneMap = {
  default: 'neutral',
  light: '1f3fb',
  mediumLight: '1f3fc',
  medium: '1f3fd',
  mediumDark: '1f3fe',
  dark: '1f3ff',
};

const convertSkinToneToCode = (skinTone) => SkinToneMap[skinTone] || SkinToneMap.default;

const convertCodeToSkinTone = (code) => Object.keys(SkinToneMap).find((key) => SkinToneMap[key] === code);

const EmojiPickerButton = ({
  onChange,
  isEditMessage,
  isDisabled,
  coachDoc,
  closeOnSelect,
  renderTrigger,
}) => {
  const [popperAnchor, setPopperAnchor] = useState(null);
  const [isPopperOpen, setIsPopperOpen] = useState(false);

  const defaultSkinTone = convertSkinToneToCode(coachDoc?.emojisSkinTone || SkinToneMap.default);

  const onSkinToneChange = useCallback((skinTone) => {
    const newSkinTone = convertCodeToSkinTone(skinTone);

    if (coachDoc) {
      coachDoc.setEmojisSkinTone(newSkinTone);
    }
  }, [coachDoc]);

  const onEmojiOpen = useCallback((event) => {
    if (isDisabled) {
      return;
    }
    setPopperAnchor(event.currentTarget);
    setIsPopperOpen(true);
  }, [isDisabled]);

  const onEmojiClick = useCallback((event) => {
    onChange(event);
    // Composer pickers stay open for multi-emoji typing; reaction pickers close after one pick.
    setIsPopperOpen(!closeOnSelect);
  }, [onChange, closeOnSelect]);

  return (
    <>
      {renderTrigger
        ? renderTrigger({ onClick: onEmojiOpen, disabled: isDisabled })
        : <StyledEmojiIcon onClick={onEmojiOpen} />}
      {popperAnchor && (
        <StyledPopper
          open={isPopperOpen}
          anchorEl={popperAnchor}
          placement={isEditMessage ? 'bottom-start' : 'top-end'}
          disablePortal
          modifiers={[
            {
              name: 'flip',
              enabled: true,
              options: {
                altBoundary: true,
                rootBoundary: 'viewport',
                padding: 10,
              },
            },
          ]}
        >
          <ClickAwayListener onClickAway={() => setIsPopperOpen(false)}>
            <div>
              <EmojiPicker
                onEmojiClick={onEmojiClick}
                previewConfig={{
                  showPreview: !isEditMessage,
                }}
                height={isEditMessage ? 300 : 400}
                defaultSkinTone={defaultSkinTone}
                onSkinToneChange={onSkinToneChange}
              />
            </div>
          </ClickAwayListener>
        </StyledPopper>
      )}
    </>
  );
};

EmojiPickerButton.propTypes = {
  onChange: PropTypes.func.isRequired,
  isEditMessage: PropTypes.bool,
  isDisabled: PropTypes.bool,
  coachDoc: PropTypes.object,
  // When true, the picker closes after a single selection (reaction use). Default keeps it open (composer use).
  closeOnSelect: PropTypes.bool,
  // Optional custom trigger: ({ onClick, disabled }) => node. Defaults to the standard emoji icon.
  renderTrigger: PropTypes.func,
};

EmojiPickerButton.defaultProps = {
  isEditMessage: false,
  isDisabled: false,
  coachDoc: null,
  closeOnSelect: false,
  renderTrigger: null,
};

export default observer(EmojiPickerButton);
